import {
  BarElement,
  CategoryScale,
  Chart,
  type ChartData,
  type ChartOptions,
  LinearScale,
  LineElement,
  Tooltip,
  type TooltipItem,
} from 'chart.js';
import ChartDataLabels, { type Context } from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import type { Dayjs } from 'dayjs';
import { useMemo, useRef } from 'react';

import useColor from '@/hooks/domain/useColor';
import { RangeUnit } from '@/utils/getDatePreset';
import {
  DATE_FORMAT,
  getFormatByRangeUnit,
  getLabelsByRange,
  getTimezoneDayjs,
  parseDateFormatByRangeUnit,
} from '@/utils/time';

Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  Tooltip,
  ChartDataLabels,
);

type State = {
  rangeUnit: RangeUnit;
  trendData: FeedbackListTrendDataT;
  dateRange: [Dayjs, Dayjs] | null;
  selectedChartBar: SelectedChartBar;
  showFeedbackCount: boolean;
  hideAxisLabel?: boolean;
};

type Props = State & {
  name: string;
  setValue: (key: any, value: any) => void;
};

const useFeedbackTrendChart = (props: Props) => {
  let selectedArea = useRef<[number, number, number, number]>([0, 0, 0, 0]);
  const { selectedChartBar, setValue } = props;
  const color = useColor();
  const chartRef = useRef<Chart<'bar'>>(null);

  const backgroundChartArea = useMemo(() => {
    return {
      id: `${props.name}_backgroundChartArea`,
      beforeDatasetsDraw(chart: Chart) {
        chart.ctx.fillStyle = color.ND08;
        chart.ctx.fillRect(...selectedArea.current);
      },
    };
  }, [color.ND08, props.name]);

  const chartData: ChartData<'bar'> = useMemo(() => {
    const labels = getLabelsByRange({
      dateRange: props.dateRange,
      rangeUnit: props.rangeUnit,
    });
    const labelMap: Record<
      string,
      {
        count: number;
      }
    > = {};
    labels.forEach(
      (label) =>
        (labelMap[label] = {
          count: 0,
        }),
    );
    const dataMap = props.trendData.feedback_trend.reduce((acc, value) => {
      let label = '';
      switch (props.rangeUnit) {
        case RangeUnit.Daily:
          label = getFormatByRangeUnit(RangeUnit.Daily)(
            getTimezoneDayjs(value.date),
          );
          break;
        case RangeUnit.Weekly:
          label = getFormatByRangeUnit(RangeUnit.Weekly)(
            getTimezoneDayjs(value.date),
          );
          break;
        case RangeUnit.Monthly:
          label = getFormatByRangeUnit(RangeUnit.Monthly)(
            getTimezoneDayjs(value.date),
          );
          break;
        default:
          break;
      }
      if (typeof acc[label] === 'undefined') {
        acc[label] = {
          count: 0,
        };
      }
      acc[label] = {
        count: acc[label].count + value.count,
      };
      return acc;
    }, labelMap);
    const result: ChartData<'bar'> = {
      labels: labels,
      datasets: [
        {
          label: 'Feedback',
          data: Object.values(dataMap).map((d) => d.count),
          backgroundColor: color.gray950,
          borderRadius: 4,
          order: 1,
          yAxisID: 'y1',
        },
      ],
    };

    return result;
  }, [color, props.dateRange, props.rangeUnit, props.trendData.feedback_trend]);

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => {
    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          enabled: true,
          callbacks: {
            footer: (tooltipItems: TooltipItem<'bar'>[]) => {
              const label = tooltipItems[0].label;

              if (props.rangeUnit === RangeUnit.Daily) {
                return '';
              }

              const { start, end } = parseDateFormatByRangeUnit(
                label,
                props.rangeUnit,
              );

              const formattedStart = getTimezoneDayjs(start).format(
                DATE_FORMAT.base,
              );
              const formattedEnd = getTimezoneDayjs(end).format(
                DATE_FORMAT.base,
              );

              return `${formattedStart} - ${formattedEnd}`;
            },
          },
        },
        datalabels: {
          align: 'end',
          anchor: 'end',
          color: color.gray600,
          display: props.showFeedbackCount
            ? (context: Context) => {
                return context.dataset.data[context.dataIndex] !== 0;
              }
            : false,
        },
        backgroundChartArea: backgroundChartArea,
      },
      scales: {
        x: props.hideAxisLabel
          ? {
              display: false,
            }
          : {
              ticks: {
                autoSkip: true,
                maxTicksLimit: 15,
              },
              grid: {
                display: false,
              },
            },
        y1: {
          grace: '10%',
          beginAtZero: true,
          ticks: {
            autoSkip: true,
            maxTicksLimit: 5,
            callback: function (value) {
              if (Math.floor(Number(value)) === Number(value)) {
                return commaNumber(value);
              }
            },
          },
        },
      },
      onResize(chart) {
        if (!selectedChartBar) return;
        if (!chart.chartArea) return;
        const meta = chart.getDatasetMeta(selectedChartBar.datasetIndex);
        const element = meta.data[selectedChartBar.dataIndex];
        if (!element) return;
        const { top, height } = chart.chartArea;
        const { x, width } = element as unknown as {
          x: number;
          width: number;
        };
        selectedArea.current = [
          x - width / 2 - width / 5,
          top,
          width * 1.4,
          height,
        ];
      },
      onClick: (e: any) => {
        const el = chartRef.current?.getElementsAtEventForMode(
          e,
          'nearest',
          { intersect: false },
          true,
        )[0];

        if (!el || selectedChartBar?.dataIndex === el.index) {
          selectedArea.current = [0, 0, 0, 0];
          setValue('selectedChartBar', null);
          return;
        }
        const chart: Chart = e.chart;
        const len = chart.data.labels?.length || 0;
        const { top, height, width } = chart.chartArea;
        const { x } = el.element;
        const areaWidth = width / len;
        selectedArea.current = [x - areaWidth / 2, top, areaWidth, height];

        const val: SelectedChartBar = {
          dataIndex: el.index,
          datasetIndex: el.datasetIndex,
        };
        setValue('selectedChartBar', val);
      },
    };
    return options;
  }, [
    color.gray600,
    props.showFeedbackCount,
    props.hideAxisLabel,
    props.rangeUnit,
    selectedChartBar,
  ]);

  const clearChartSelect = () => {
    selectedArea.current = [0, 0, 0, 0];
    props.setValue('selectedChartBar', null);
  };

  return {
    trendChartData: chartData,
    trendChartOptions: barChartOptions,
    clearChartSelect,
    chartRef,
    backgroundChartArea,
  };
};

export default useFeedbackTrendChart;
